import { create } from 'zustand';
import type {
  VideoAnalysis,
  VideoAnalysisProvider,
  VideoListItem,
} from '@sr-test-wizard/shared';
import { runVideosApi } from '../lib/runVideosApi';

/**
 * Progress snapshot emitted by the server for an active extract or analyze
 * job. Both share the same shape; the `kind` field discriminates which
 * stage the UI should highlight.
 */
export interface VideoJobProgress {
  kind: 'extract' | 'analyze';
  current: number;
  total: number | null;
  completed: number;
  errors: number;
  status: 'started' | 'progress' | 'completed' | 'failed';
  message?: string;
}

export interface VideoNativeStageProgress {
  stage: 'upload' | 'analyze' | 'cleanup';
  status: 'started' | 'progress' | 'completed' | 'failed';
  message?: string;
}

function analysisKey(slug: string, provider: VideoAnalysisProvider): string {
  return `${slug}::${provider}`;
}

interface VideosState {
  runId: string | null;
  list: VideoListItem[];
  listLoading: boolean;
  error: string | null;
  /** Currently selected video slug (left-pane player + right-pane analysis). */
  selectedSlug: string | null;
  selectedProvider: VideoAnalysisProvider;
  /** Loaded analysis JSON per (slug, provider), lazy. */
  analysisByKey: Record<string, VideoAnalysis | null | undefined>;
  /** Per-(slug, provider) active analysis progress; extract still keyed by slug. */
  progressByKey: Record<string, VideoJobProgress | undefined>;
  nativeStageByKey: Record<string, VideoNativeStageProgress | undefined>;
  /** Current playback second of the player (rounded to whole seconds). */
  currentSecond: number;

  load: (runId: string) => Promise<void>;
  setProvider: (provider: VideoAnalysisProvider) => void;
  select: (slug: string | null) => Promise<void>;
  loadAnalysis: (slug: string, provider?: VideoAnalysisProvider) => Promise<void>;
  upload: (file: File, onProgress?: (pct: number) => void) => Promise<VideoListItem | null>;
  remove: (slug: string) => Promise<void>;
  startExtract: (slug: string) => Promise<void>;
  startAnalyze: (
    slug: string,
    force?: boolean,
    provider?: VideoAnalysisProvider,
  ) => Promise<void>;
  setCurrentSecond: (second: number) => void;
  applyExtractProgress: (slug: string, snapshot: VideoJobProgress) => void;
  applyAnalyzeProgress: (
    slug: string,
    provider: VideoAnalysisProvider,
    snapshot: VideoJobProgress,
  ) => void;
  applyNativeStageProgress: (
    slug: string,
    provider: VideoAnalysisProvider,
    stage: VideoNativeStageProgress,
  ) => void;
  clear: () => void;
}

function patchListEntry(
  list: VideoListItem[],
  slug: string,
  patch: Partial<VideoListItem>,
): VideoListItem[] {
  return list.map((item) => (item.slug === slug ? { ...item, ...patch } : item));
}

/**
 * Owns the state for the Gameplay Video Analyzer page. The WebSocket tray
 * pushes extract / analyze progress here via `applyExtractProgress` /
 * `applyAnalyzeProgress` so the page never has to re-poll.
 */
export const useVideosStore = create<VideosState>((set, get) => ({
  runId: null,
  list: [],
  listLoading: false,
  error: null,
  selectedSlug: null,
  selectedProvider: 'gemini-native',
  analysisByKey: {},
  progressByKey: {},
  nativeStageByKey: {},
  currentSecond: 0,

  async load(runId) {
    const state = get();
    const isDifferentRun = state.runId !== null && state.runId !== runId;
    set({
      runId,
      listLoading: true,
      error: null,
      ...(isDifferentRun
        ? {
            list: [],
            selectedSlug: null,
            analysisByKey: {},
            progressByKey: {},
            nativeStageByKey: {},
            currentSecond: 0,
          }
        : {}),
    });
    try {
      const data = await runVideosApi.list(runId);
      set((s) => {
        // Default-select the first video so the page lights up immediately.
        const selectedSlug =
          s.selectedSlug && data.items.some((v) => v.slug === s.selectedSlug)
            ? s.selectedSlug
            : data.items[0]?.slug ?? null;
        return { list: data.items, selectedSlug };
      });
      const selected = get().selectedSlug;
      if (selected) {
        await get().loadAnalysis(selected, get().selectedProvider);
      }
    } catch (err) {
      set({ error: (err as Error).message });
    } finally {
      set({ listLoading: false });
    }
  },

  setProvider(provider) {
    const { selectedSlug } = get();
    set({ selectedProvider: provider });
    if (selectedSlug) {
      void get().loadAnalysis(selectedSlug, provider);
    }
  },

  async select(slug) {
    set({ selectedSlug: slug, currentSecond: 0 });
    if (slug) {
      await get().loadAnalysis(slug, get().selectedProvider);
    }
  },

  async loadAnalysis(slug, provider) {
    const { runId, selectedProvider } = get();
    if (!runId) return;
    const p = provider ?? selectedProvider;
    try {
      const data = await runVideosApi.analysis(runId, slug, p);
      set((s) => ({
        analysisByKey: { ...s.analysisByKey, [analysisKey(slug, p)]: data.analysis },
        list: patchListEntry(s.list, slug, {
          analysisExists:
            data.availableProviders['openai-frames'] || data.availableProviders['gemini-native'],
          analysisByProvider: data.availableProviders,
        }),
      }));
    } catch (err) {
      set({ error: (err as Error).message });
    }
  },

  async upload(file, onProgress) {
    const { runId } = get();
    if (!runId) throw new Error('No run loaded');
    try {
      const item = await runVideosApi.upload(runId, file, onProgress);
      set((s) => ({
        list: [
          ...s.list.filter((v) => v.slug !== item.slug),
          {
            slug: item.slug,
            videoFile: item.videoFile,
            sizeBytes: item.sizeBytes,
            uploadedAt: item.uploadedAt,
            frameCount: item.frameCount,
            durationSeconds: item.durationSeconds,
            analysisExists: item.analysisExists,
            analysisByProvider: item.analysisByProvider,
            extractInProgress: item.extractInProgress,
            analyzeInProgress: item.analyzeInProgress,
          },
        ].sort((a, b) => a.slug.localeCompare(b.slug)),
        selectedSlug: item.slug,
      }));
      return get().list.find((v) => v.slug === item.slug) ?? null;
    } catch (err) {
      set({ error: (err as Error).message });
      return null;
    }
  },

  async remove(slug) {
    const { runId } = get();
    if (!runId) return;
    try {
      await runVideosApi.delete(runId, slug);
      set((s) => {
        const list = s.list.filter((v) => v.slug !== slug);
        const selectedSlug = s.selectedSlug === slug ? list[0]?.slug ?? null : s.selectedSlug;
        const analysisByKey = { ...s.analysisByKey };
        delete analysisByKey[analysisKey(slug, 'openai-frames')];
        delete analysisByKey[analysisKey(slug, 'gemini-native')];
        const progressByKey = { ...s.progressByKey };
        delete progressByKey[analysisKey(slug, 'openai-frames')];
        delete progressByKey[analysisKey(slug, 'gemini-native')];
        delete progressByKey[`${slug}::extract`];
        const nativeStageByKey = { ...s.nativeStageByKey };
        delete nativeStageByKey[analysisKey(slug, 'gemini-native')];
        return { list, selectedSlug, analysisByKey, progressByKey, nativeStageByKey };
      });
    } catch (err) {
      set({ error: (err as Error).message });
    }
  },

  async startExtract(slug) {
    const { runId } = get();
    if (!runId) return;
    set((s) => ({
      list: patchListEntry(s.list, slug, { extractInProgress: true }),
      progressByKey: {
        ...s.progressByKey,
        [`${slug}::extract`]: {
          kind: 'extract',
          current: 0,
          total: null,
          completed: 0,
          errors: 0,
          status: 'started',
        },
      },
    }));
    try {
      await runVideosApi.extract(runId, slug);
    } catch (err) {
      set({ error: (err as Error).message });
    }
  },

  async startAnalyze(slug, force, provider) {
    const { runId, selectedProvider } = get();
    if (!runId) return;
    const p = provider ?? selectedProvider;
    set((s) => ({
      list: patchListEntry(s.list, slug, { analyzeInProgress: true }),
      progressByKey: {
        ...s.progressByKey,
        [analysisKey(slug, p)]: {
          kind: 'analyze',
          current: 0,
          total: 0,
          completed: 0,
          errors: 0,
          status: 'started',
        },
      },
    }));
    try {
      await runVideosApi.analyze(runId, slug, force ?? false, p);
    } catch (err) {
      set({ error: (err as Error).message });
    }
  },

  setCurrentSecond(second) {
    if (get().currentSecond === second) return;
    set({ currentSecond: second });
  },

  applyExtractProgress(slug, snapshot) {
    set((s) => {
      const next: VideosState['progressByKey'] = {
        ...s.progressByKey,
        [`${slug}::extract`]: snapshot,
      };
      const finished = snapshot.status === 'completed' || snapshot.status === 'failed';
      // Patch the list-row flags so the upload/list area reflects the
      // current state without an extra fetch. When extraction completes
      // we also bump the frame count so the analyze button enables.
      const list = patchListEntry(s.list, slug, {
        extractInProgress: !finished,
        frameCount:
          snapshot.status === 'completed' && snapshot.total
            ? snapshot.total
            : s.list.find((v) => v.slug === slug)?.frameCount ?? 0,
      });
      if (finished) {
        // Drop the progress entry after a short delay so the UI can show
        // the "completed" tick briefly. We keep it in the map but flag
        // the status; the page handles the fade-out.
      }
      return { progressByKey: next, list };
    });
  },

  applyAnalyzeProgress(slug, provider, snapshot) {
    set((s) => {
      const key = analysisKey(slug, provider);
      const next: VideosState['progressByKey'] = { ...s.progressByKey, [key]: snapshot };
      const finished = snapshot.status === 'completed' || snapshot.status === 'failed';
      const currentItem = s.list.find((v) => v.slug === slug);
      const analysisByProvider = {
        'openai-frames': currentItem?.analysisByProvider?.['openai-frames'] ?? false,
        'gemini-native': currentItem?.analysisByProvider?.['gemini-native'] ?? false,
      };
      if (snapshot.status === 'completed') {
        analysisByProvider[provider] = true;
      }
      const list = patchListEntry(s.list, slug, {
        analyzeInProgress: !finished,
        analysisByProvider,
        analysisExists: analysisByProvider['openai-frames'] || analysisByProvider['gemini-native'],
      });
      return { progressByKey: next, list };
    });
    if (snapshot.status === 'completed') {
      // Pull the freshly-written JSON so the right pane re-renders with
      // the final rows.
      void get().loadAnalysis(slug, provider);
    }
  },

  applyNativeStageProgress(slug, provider, stage) {
    set((s) => ({
      nativeStageByKey: { ...s.nativeStageByKey, [analysisKey(slug, provider)]: stage },
    }));
  },

  clear() {
    set({
      runId: null,
      list: [],
      selectedSlug: null,
      selectedProvider: 'gemini-native',
      analysisByKey: {},
      progressByKey: {},
      nativeStageByKey: {},
      currentSecond: 0,
      error: null,
      listLoading: false,
    });
  },
}));
